import { useMemo, useState } from 'react'
import { collection, limit, orderBy, query, where } from 'firebase/firestore'
import { BellRing, Check, Clock, FileUp, X } from 'lucide-react'
import { db } from '@/lib/firebase'
import { Badge, Button, Card, EmptyState, SectionTitle, Spinner } from '@/components/ui'
import { QueryError } from '@/components/QueryError'
import { Modal } from '@/components/Modal'
import { useQuerySnapshot } from '@/hooks/useQuerySnapshot'
import { useToast } from '@/components/Toast'
import { cancelOrder, markOrderPaid, remindOrder } from '@/lib/api'
import { authErrorMessage } from '@/lib/authErrors'
import { ReconcileModal } from './ReconcileModal'
import { COLLECTIONS } from '@shared/model'
import type { KnownOrder } from '@shared/swishReport'
import { ORDER_STATUS_LABELS, type Order } from '@shared/orders'
import { formatDateTime, formatSek } from '@/lib/format'

/**
 * Beställningar för lagledaren.
 *
 * Ersätter jakten på betalningar med en lista. Skillnaden mot idag är inte
 * bara att listan finns – det är att ingen fått något häfte ännu, så en
 * obetald rad är inte en skuld att driva in utan bara en beställning som
 * väntar. Därför heter knappen "Påminn" och inte "Kräv".
 *
 * Bekräftelsen delar ut koderna och mejlar dem. Det görs av Cloud Function,
 * aldrig härifrån – annars hade vem som helst kunnat sätta status till betald
 * och få häften gratis.
 */
export function OrdersSection({ teamId, bookletId }: { teamId: string; bookletId: string }) {
  const toast = useToast()
  const [busyReference, setBusyReference] = useState<string | null>(null)
  const [cancelling, setCancelling] = useState<Order | null>(null)
  const [preview, setPreview] = useState<{
    to: string
    subject: string
    text: string
  } | null>(null)
  const [reconciling, setReconciling] = useState(false)

  const ordersQuery = useMemo(
    () =>
      teamId && bookletId
        ? query(
            collection(db, COLLECTIONS.orders),
            where('teamId', '==', teamId),
            where('bookletId', '==', bookletId),
            orderBy('createdAt', 'desc'),
            limit(200),
          )
        : null,
    [teamId, bookletId],
  )
  const { data: orders, loading, error } = useQuerySnapshot<Order>(ordersQuery)

  const waiting = useMemo(
    () => orders.filter((order) => order.status === 'awaitingPayment'),
    [orders],
  )
  const outstanding = useMemo(
    () => waiting.reduce((sum, order) => sum + order.totalSek, 0),
    [waiting],
  )

  async function handlePaid(order: Order) {
    setBusyReference(order.id)
    try {
      const result = await markOrderPaid({ reference: order.id })
      toast.success(
        result.alreadyPaid
          ? 'Beställningen var redan bekräftad.'
          : `${result.codeIds.length} koder skickade till ${order.buyerEmail}.`,
      )
    } catch (caught) {
      toast.error(authErrorMessage(caught))
    } finally {
      setBusyReference(null)
    }
  }

  async function handleRemind(order: Order) {
    setBusyReference(order.id)
    try {
      const result = await remindOrder({ reference: order.id })
      toast.success(`Påminnelse skickad till ${order.buyerEmail}.`)
      // Testbeställningar får mejlet tillbaka, så att den som demonstrerar
      // kan visa vad föräldern faktiskt läser i stället för att beskriva det.
      if (result.preview) setPreview(result.preview)
    } catch (caught) {
      toast.error(authErrorMessage(caught))
    } finally {
      setBusyReference(null)
    }
  }

  return (
    <section className="space-y-3">
      <SectionTitle
        action={
          orders.length > 0 && (
            <Button
              size="sm"
              variant="quiet"
              icon={<FileUp size={15} />}
              onClick={() => setReconciling(true)}
            >
              Stäm av mot Swish
            </Button>
          )
        }
      >
        Beställningar
        {waiting.length > 0 && (
          <Badge tone="warning" className="ml-2">
            {waiting.length} väntar · {formatSek(outstanding)}
          </Badge>
        )}
      </SectionTitle>

      {error ? (
        <QueryError error={error} />
      ) : loading ? (
        <Spinner />
      ) : orders.length === 0 ? (
        <EmptyState
          title="Inga beställningar än"
          description="När en förälder beställt häften dyker beställningen upp här. Koderna delas ut först när du bekräftat betalningen."
        />
      ) : (
        <ul className="space-y-2">
          {orders.map((order) => (
            <li key={order.id}>
              <Card className="flex flex-wrap items-center gap-3 p-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-sm font-bold tracking-widest">{order.id}</span>
                    <Badge
                      tone={
                        order.status === 'paid'
                          ? 'success'
                          : order.status === 'cancelled'
                            ? 'neutral'
                            : 'warning'
                      }
                    >
                      {ORDER_STATUS_LABELS[order.status]}
                    </Badge>
                  </div>
                  <p className="mt-1 truncate text-sm font-semibold">
                    {order.quantity} häften · {formatSek(order.totalSek)}
                  </p>
                  <p className="truncate text-xs text-muted">
                    {order.buyerName} ({order.buyerEmail}) för {order.playerName}
                  </p>
                  <p className="mt-0.5 flex items-center gap-1 text-xs text-faint">
                    <Clock size={11} aria-hidden />
                    {formatDateTime(order.createdAt as never)}
                    {order.remindedAt ? ' · påmind' : ''}
                  </p>
                </div>

                {order.status === 'awaitingPayment' && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="quiet"
                      icon={<BellRing size={15} />}
                      loading={busyReference === order.id}
                      onClick={() => void handleRemind(order)}
                    >
                      Påminn
                    </Button>
                    <Button
                      size="sm"
                      variant="quiet"
                      icon={<X size={15} />}
                      onClick={() => setCancelling(order)}
                    >
                      Avbryt
                    </Button>
                    <Button
                      size="sm"
                      icon={<Check size={15} />}
                      loading={busyReference === order.id}
                      onClick={() => void handlePaid(order)}
                    >
                      Betald
                    </Button>
                  </div>
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}

      {preview && (
        <Modal
          open
          onClose={() => setPreview(null)}
          title="Så här ser påminnelsen ut"
          size="md"
          footer={
            <Button fullWidth onClick={() => setPreview(null)}>
              Stäng
            </Button>
          }
        >
          <div className="space-y-3">
            <p className="text-sm text-muted">
              Mejlet gick till <strong className="text-ink">{preview.to}</strong>. Visas bara för
              provbeställningar.
            </p>
            <div className="rounded-card border border-border bg-surface-2 p-4">
              <p className="font-display font-extrabold">{preview.subject}</p>
              {/* Radbrytningarna är innehållet: mejlet är skrivet som text och
                  ska läsas som det står, inte flyta ihop till ett stycke. */}
              <pre className="mt-2 font-sans text-sm whitespace-pre-wrap text-ink">
                {preview.text}
              </pre>
            </div>
          </div>
        </Modal>
      )}

      {cancelling && (
        <CancelModal order={cancelling} onClose={() => setCancelling(null)} />
      )}

      {reconciling && (
        <ReconcileModal
          orders={orders.map<KnownOrder>((order) => ({
            reference: order.id,
            totalSek: order.totalSek,
            status: order.status,
            buyerName: order.buyerName,
          }))}
          isTest={orders.some((order) => order.isTest === true)}
          onClose={() => setReconciling(false)}
        />
      )}
    </section>
  )
}

function CancelModal({ order, onClose }: { order: Order; onClose: () => void }) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)

  async function handleCancel() {
    setBusy(true)
    try {
      await cancelOrder({ reference: order.id, reason: 'Avbruten av lagledaren' })
      toast.success('Beställningen är avbruten.')
      onClose()
    } catch (caught) {
      toast.error(authErrorMessage(caught))
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Avbryt beställningen"
      size="sm"
      footer={
        <div className="flex gap-2">
          <Button variant="quiet" fullWidth onClick={onClose}>
            Behåll
          </Button>
          <Button fullWidth loading={busy} onClick={() => void handleCancel()}>
            Avbryt beställningen
          </Button>
        </div>
      }
    >
      <p className="text-sm text-muted">
        {order.buyerName} beställde {order.quantity} häften för {formatSek(order.totalSek)}.
        Beställningen markeras som avbruten och inga koder delas ut. Föräldern kan beställa på nytt
        när som helst.
      </p>
    </Modal>
  )
}
